// ============================================================================
// As 9 etapas canônicas do onboarding (ver §8 do brief).
// Cada etapa mapeia para uma coluna timestamp em onboarding_pipeline.
// Etapas 2 e 3 são AUTOMÁTICAS (chat→wrapper / sync D4Sign). 1 e 4-9 manuais.
// ============================================================================

import type { StatusContrato } from "./types";

/** Campos de timestamp presentes tanto em BoardRow quanto em OnboardingPipeline. */
export type TimestampField =
  | "handoff_em"
  | "contrato_enviado_em"
  | "contrato_assinado_em"
  | "whatsapp_grupo_em"
  | "onboarding_msg_em"
  | "reuniao_onboarding_em"
  | "onboarding_concluido_em"
  | "treino_comercial_em"
  | "treino_conteudo_em";

export interface StageDef {
  n: number;
  label: string;
  /** Coluna de timestamp em onboarding_pipeline que marca a conclusão. */
  campo: TimestampField;
  /** true = preenchida por automação (chat/sync); read-only no app. */
  automatica: boolean;
  /** Campos extras que o "marcar concluído" coleta nesta etapa. */
  extras?: ("data" | "link" | "grupo_id")[];
  /** Etapa do grupo WhatsApp: resolve o grupo a partir do link de convite (Z-API). */
  resolveGrupo?: boolean;
  descricao: string;
}

export const STAGES: StageDef[] = [
  { n: 1, label: "Handoff registrado", campo: "handoff_em", automatica: false, descricao: "Comercial registrou o handoff (link, plano, valor, notas)." },
  { n: 2, label: "Contrato enviado p/ criação", campo: "contrato_enviado_em", automatica: true, descricao: "Chat → wrapper n8n gerou o contrato no D4Sign." },
  { n: 3, label: "Contrato assinado", campo: "contrato_assinado_em", automatica: true, descricao: "Sync D4Sign detectou todas as assinaturas (contrato ativo)." },
  { n: 4, label: "Grupo WhatsApp criado", campo: "whatsapp_grupo_em", automatica: false, resolveGrupo: true, descricao: "Grupo de WhatsApp do cliente criado." },
  { n: 5, label: "Mensagem de onboarding enviada", campo: "onboarding_msg_em", automatica: false, descricao: "Mensagem de boas-vindas / onboarding enviada ao cliente." },
  { n: 6, label: "Reunião de onboarding agendada", campo: "reuniao_onboarding_em", automatica: false, extras: ["data", "link"], descricao: "Reunião de onboarding marcada (data + link)." },
  { n: 7, label: "Onboarding concluído", campo: "onboarding_concluido_em", automatica: false, descricao: "Reunião de onboarding realizada e concluída." },
  { n: 8, label: "Treinamento comercial concluído", campo: "treino_comercial_em", automatica: false, descricao: "Treinamento comercial entregue." },
  { n: 9, label: "Treinamento de conteúdo concluído", campo: "treino_conteudo_em", automatica: false, descricao: "Treinamento de conteúdo entregue. Onboarding completo." },
];

export const TOTAL_STAGES = STAGES.length;

/** Mapa rápido etapa → coluna timestamp. */
export const STAGE_CAMPO: Record<number, TimestampField> = Object.fromEntries(
  STAGES.map((s) => [s.n, s.campo])
);

type ComTimestamps = Partial<Record<TimestampField, string | null>> & { stage?: number | null };

/**
 * Etapa de EXIBIÇÃO = maior entre `stage` e a etapa implícita pelos timestamps
 * preenchidos. Garante que progresso (manual ou automático) nunca regrida —
 * ex.: se contrato_assinado_em está preenchido, exibe >= 3 mesmo que stage=1.
 */
export function derivarStageExibicao(row: ComTimestamps): number {
  let derivada = 0;
  for (const s of STAGES) {
    if (row[s.campo]) derivada = Math.max(derivada, s.n);
  }
  return Math.max(derivada, row.stage ?? 0, 1);
}

// ----------------------------------------------------------------------------
// Badge de status de contrato
// ----------------------------------------------------------------------------

export type BadgeTone = "neutral" | "info" | "warning" | "success" | "danger";

export interface StatusBadge {
  label: string;
  tone: BadgeTone;
}

const STATUS_BADGE: Record<StatusContrato, StatusBadge> = {
  rascunho: { label: "Rascunho", tone: "neutral" },
  em_revisao_interna: { label: "Em revisão", tone: "info" },
  aguardando_assinatura: { label: "Aguardando assinatura", tone: "warning" },
  ativo: { label: "Ativo", tone: "success" },
  rescindido: { label: "Rescindido", tone: "danger" },
  cancelado: { label: "Cancelado", tone: "danger" },
};

export function mapStatusContratoBadge(status: StatusContrato | null): StatusBadge {
  if (!status) return { label: "Sem contrato", tone: "neutral" };
  return STATUS_BADGE[status] ?? { label: status, tone: "neutral" };
}

/** Badge do contrato considerando o OK manual da equipe (confirmação tem precedência). */
export function badgeContrato(status: StatusContrato | null, confirmadoEm: string | null): StatusBadge {
  if (confirmadoEm) return { label: "Assinado · confirmado", tone: "success" };
  return mapStatusContratoBadge(status);
}

/** Dias desde a última atualização (para filtro "parados há X dias"). */
export function diasParado(atualizadoEm: string | null, agoraISO?: string): number | null {
  if (!atualizadoEm) return null;
  const base = agoraISO ? new Date(agoraISO) : new Date();
  const t = new Date(atualizadoEm).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((base.getTime() - t) / 86_400_000);
}
